import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  accessRoles,
  businessTemplates,
  tenantApi,
  userAccess,
  userDirectionAccess,
  usersAdmin,
  type BusinessTemplate,
  type UserAccessGrant,
  type UserDirectionAccessGrant,
} from '@/api/admin';
import { orgs } from '@/api/catalogs';
import { projects } from '@/api/projects';
import { moduleName } from '@/utils/moduleNames';
import { humanizeBackendError } from '@/utils/backendError';
import BusinessTemplateEditorDialog from './admin/BusinessTemplateEditorDialog';
import TeamSection from './admin/TeamSection';
import SecuritySection from './admin/SecuritySection';
import RolesSection from './team/RolesSection';
import OwnersSection from './admin/OwnersSection';

type Tab = 'tenant' | 'users' | 'security' | 'access' | 'directions' | 'roles' | 'owners' | 'templates';

export default function AdminPage() {
  // ?tab=members з redirect /team — підхоплюємо, інакше дефолт 'tenant'.
  const initialTab = ((): Tab => {
    if (typeof window === 'undefined') return 'tenant';
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t === 'members' || t === 'team' || t === 'users') return 'users';
    if (t && ['tenant','users','security','access','directions','roles','owners','templates'].includes(t)) return t as Tab;
    return 'tenant';
  })();
  const [tab, setTab] = useState<Tab>(initialTab);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Адміністрування акаунта</h2>
      </div>
      {/* ч.961 (зауваження власника): вкладки за каноном проєкту — .seg-tabs. */}
      <div className="seg-tabs">
        {(
          [
            ['tenant', 'Акаунт'],
            ['users', 'Користувачі і доступи'],
            ['security', 'Безпека'],
            ['access', 'Доступи (юрособи × ролі)'],
            ['directions', 'Напрямки'],
            ['roles', 'Ролі'],
            ['owners', 'Відповідальні'],
            ['templates', 'Шаблони бізнесу'],
          ] as [Tab, string][]
        ).map(([k, label]) => (
          <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'tenant' && <TenantTab />}
      {tab === 'users' && <TeamSection />}
      {tab === 'security' && <SecuritySection />}
      {tab === 'access' && <AccessTab />}
      {tab === 'directions' && <DirectionAccessTab />}
      {tab === 'roles' && <RolesSection />}
      {tab === 'owners' && <OwnersSection />}
      {tab === 'templates' && <TemplatesTab />}
    </div>
  );
}

function TenantTab() {
  const q = useQuery({ queryKey: ['admin-tenant'], queryFn: () => tenantApi.get() });
  if (q.isLoading) return <p>Завантаження…</p>;
  if (!q.data) return null;
  const t = q.data;
  return (
    <table style={{ width: 'auto', fontSize: '0.9rem' }}>
      <tbody>
        <tr><td style={td}>Назва:</td><td><b>{t.name}</b></td></tr>
        <tr><td style={td}>Тариф:</td><td>{t.plan}</td></tr>
        <tr><td style={td}>Статус:</td><td>{t.status}</td></tr>
        <tr><td style={td}>Ліміт юросіб:</td><td>{t.max_organizations}</td></tr>
        <tr><td style={td}>Ліміт користувачів:</td><td>{t.max_users}</td></tr>
        <tr><td style={td}>Ліміт документів/міс:</td><td>{t.max_documents_month ?? '—'}</td></tr>
        <tr><td style={td}>Білінг email:</td><td>{t.billing_email ?? '—'}</td></tr>
      </tbody>
    </table>
  );
}

function AccessTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-access'], queryFn: () => userAccess.list() });
  const usersQ = useQuery({ queryKey: ['admin-users'], queryFn: () => usersAdmin.list() });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const rolesQ = useQuery({ queryKey: ['admin-roles'], queryFn: () => accessRoles.list() });
  const [form, setForm] = useState<UserAccessGrant>({ user_id: '', organization_id: '', access_role_id: '' });
  const grant = useMutation({
    mutationFn: () => userAccess.grant(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-access'] });
      setForm({ user_id: '', organization_id: '', access_role_id: '' });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => userAccess.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-access'] }),
  });

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        OWNER/ADMIN мають доступ до всіх юросіб автоматично — їх тут не показуємо.
        Записи нижче — це доступи MEMBER'ів. Змінити доступи одного
        користувача (у т.ч. ще не прийнятого запрошення) зручніше кнопкою
        «доступи» на вкладці «Користувачі і доступи».
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0.5rem', marginBottom: '1rem' }}>
        <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
          <option value="">— Користувач —</option>
          {usersQ.data?.filter((u) => u.tenant_role === 'MEMBER' && u.is_active).map((u) => (
            <option key={u.id} value={u.id}>{u.email} ({u.full_name})</option>
          ))}
        </select>
        <select value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
          <option value="">— Юрособа —</option>
          {orgsQ.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <select value={form.access_role_id} onChange={(e) => setForm({ ...form, access_role_id: e.target.value })}>
          <option value="">— Роль —</option>
          {rolesQ.data?.filter((r) => r.status === 'ACTIVE').map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        <button
          onClick={() => grant.mutate()}
          disabled={!form.user_id || !form.organization_id || !form.access_role_id}
        >
          + Видати
        </button>
      </div>

      <table style={{ width: '100%', fontSize: '0.9rem' }}>
        <thead>
          <tr><th style={th}>Користувач</th><th style={th}>Юрособа</th><th style={th}>Роль</th><th style={th}>Видано</th><th style={th}></th></tr>
        </thead>
        <tbody>
          {q.data?.map((a) => (
            <tr key={a.id}>
              <td style={td}>{a.user_email} <span style={{ color: 'var(--muted)' }}>({a.user_full_name})</span></td>
              <td style={td}>{a.organization_name}</td>
              <td style={td}>{a.access_role_code}</td>
              <td style={td}>{new Date(a.granted_at).toLocaleDateString('uk-UA')}</td>
              <td style={td}>
                <button onClick={() => revoke.mutate(a.id)} style={{ fontSize: '0.8rem' }}>Відкликати</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DirectionAccessTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-uda'], queryFn: () => userDirectionAccess.list() });
  const usersQ = useQuery({ queryKey: ['admin-users'], queryFn: () => usersAdmin.list() });
  const directionsQ = useQuery({
    queryKey: ['admin-directions'],
    queryFn: () => projects.list({ kind: 'DIRECTION', status: 'ACTIVE', limit: 500 }),
  });
  const [form, setForm] = useState<UserDirectionAccessGrant>({ user_id: '', project_id: '' });
  const grant = useMutation({
    mutationFn: () => userDirectionAccess.grant(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-uda'] });
      setForm({ user_id: '', project_id: '' });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => userDirectionAccess.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-uda'] }),
  });

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        Обмеження користувача напрямками. Правило: <b>пусто</b> = MEMBER бачить
        усі напрямки доступних йому юросіб; <b>є записи</b> = бачить лише ці.
        OWNER/ADMIN — завжди всі. Цей таб впливає лише на kind=DIRECTION
        (звичайні «проєкти» обмежувати не потрібно).
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', marginBottom: '1rem' }}>
        <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
          <option value="">— Користувач —</option>
          {usersQ.data?.filter((u) => u.tenant_role === 'MEMBER' && u.is_active).map((u) => (
            <option key={u.id} value={u.id}>{u.email} ({u.full_name})</option>
          ))}
        </select>
        <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
          <option value="">— Напрямок —</option>
          {directionsQ.data?.items.map((p) => (
            <option key={p.id} value={p.id}>{p.name} — {p.code}</option>
          ))}
        </select>
        <button
          onClick={() => grant.mutate()}
          disabled={!form.user_id || !form.project_id}
        >
          + Видати
        </button>
      </div>

      <table style={{ width: '100%', fontSize: '0.9rem' }}>
        <thead>
          <tr>
            <th style={th}>Користувач</th>
            <th style={th}>Напрямок</th>
            <th style={th}>Видано</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {q.data?.map((a) => (
            <tr key={a.id}>
              <td style={td}>{a.user_email} <span style={{ color: 'var(--muted)' }}>({a.user_full_name})</span></td>
              <td style={td}><code>{a.project_code}</code> — {a.project_name}</td>
              <td style={td}>{new Date(a.granted_at).toLocaleDateString('uk-UA')}</td>
              <td style={td}>
                <button onClick={() => revoke.mutate(a.id)} style={{ fontSize: '0.8rem' }}>Відкликати</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TemplatesTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-templates'], queryFn: () => businessTemplates.list() });
  const [editing, setEditing] = useState<BusinessTemplate | null | undefined>(undefined);

  const removeMut = useMutation({
    mutationFn: (id: string) => businessTemplates.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-templates'] });
      qc.invalidateQueries({ queryKey: ['business-templates'] });
    },
  });

  function handleDelete(t: BusinessTemplate) {
    if (!confirm(`Видалити кастомний шаблон «${t.name}»?\n\nЯкщо шаблон використовується юрособами — видалення буде відхилено.`)) return;
    removeMut.mutate(t.id, {
      onError: (e) => alert(humanizeBackendError(e)),
    });
  }

  const systemTemplates = (q.data ?? []).filter((t) => t.is_system);
  const customTemplates = (q.data ?? []).filter((t) => !t.is_system);

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Системні шаблони ({systemTemplates.length})</h3>
        <button className="primary" onClick={() => setEditing(null)}>
          + Створити кастомний шаблон
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {systemTemplates.map((t) => (
          <TemplateCard
            key={t.id}
            t={t}
            onView={() => setEditing(t)}
          />
        ))}
      </div>

      <h3 style={{ marginBottom: '0.5rem' }}>
        Кастомні шаблони ({customTemplates.length})
      </h3>
      {customTemplates.length === 0 && (
        <p style={{ color: 'var(--text-secondary)' }}>
          Кастомних шаблонів поки немає. Натисніть «+ Створити кастомний шаблон»
          щоб клонувати системний (або інший кастомний) і налаштувати під свій
          бізнес.
        </p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
        {customTemplates.map((t) => (
          <TemplateCard
            key={t.id}
            t={t}
            onView={() => setEditing(t)}
            onDelete={() => handleDelete(t)}
          />
        ))}
      </div>

      {editing !== undefined && (
        <BusinessTemplateEditorDialog
          template={editing}
          allTemplates={q.data ?? []}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            qc.invalidateQueries({ queryKey: ['admin-templates'] });
            qc.invalidateQueries({ queryKey: ['business-templates'] });
          }}
        />
      )}
    </div>
  );
}

function TemplateCard({
  t, onView, onDelete,
}: { t: BusinessTemplate; onView: () => void; onDelete?: () => void }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem' }}>
      <div style={{ fontWeight: 700, marginBottom: '0.3rem' }}>{t.name}</div>
      <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
        <code>{t.code}</code>{t.is_system ? ' · системний' : ' · кастомний'}
      </div>
      <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem', minHeight: '2.4em' }}>
        {t.description}
      </div>
      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
        Модулі ({t.enabled_modules.length}):{' '}
        {t.enabled_modules.map(moduleName).join(', ')}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={onView} style={{ flex: 1 }}>
          {t.is_system ? 'Переглянути' : 'Редагувати'}
        </button>
        {onDelete && (
          <button
            onClick={onDelete}
            style={{ color: 'var(--danger, #c00)' }}
            aria-label="Видалити"
          >
            Видалити
          </button>
        )}
      </div>
    </div>
  );
}

const td: React.CSSProperties = { padding: '0.4rem 0.8rem 0.4rem 0', verticalAlign: 'top' };
const th: React.CSSProperties = { padding: '0.4rem', textAlign: 'left', borderBottom: '1px solid var(--border)' };
